import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ethers } from 'ethers';
import { CONTRACT_ADDRESS, MOOD_NFT_ABI, MOOD_METADATA, MOOD_MAPPING, connectBlockchain } from './contractDetails';
import './App.css';

function App() {
  // State management
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [contract, setContract] = useState(null);
  const [detectedMood, setDetectedMood] = useState(null);
  const [userNFTs, setUserNFTs] = useState([]);
  const [error, setError] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [isLoadingNFTs, setIsLoadingNFTs] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);

  // Refs for camera elements
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // --- BLOCKCHAIN INTERACTIONS ---

  const connectWallet = useCallback(async () => {
    setIsConnecting(true);
    setError('');
    try {
      const { provider, signer, contract, address } = await connectBlockchain();
      setProvider(provider);
      setContract(contract);
      setAccount(address);
      await loadUserNFTs(contract, address);
    } catch (err) {
      setError(err.message || 'Failed to connect wallet.');
      console.error(err);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  const loadUserNFTs = async (contractInstance, userAddress) => {
    setIsLoadingNFTs(true);
    try {
      const tokenIds = await contractInstance.getUserNFTs(userAddress);
      const nftDetailsPromises = tokenIds.map(id => contractInstance.getNFTDetails(id));
      const nftDetails = await Promise.all(nftDetailsPromises);
      
      const formattedNfts = nftDetails.map(nft => ({
        tokenId: Number(nft.tokenId),
        mood: Number(nft.mood),
        timestamp: Number(nft.timestamp),
        tokenURI: nft.tokenURI,
      })).sort((a, b) => b.tokenId - a.tokenId); // Sort by most recent
      
      setUserNFTs(formattedNfts);
    } catch (err) {
      console.error("Error loading NFTs:", err);
      setError("Could not load your NFT collection.");
    } finally {
      setIsLoadingNFTs(false);
    }
  };

  const handleMintNFT = async () => {
    if (!contract || !account || detectedMood === null) return;

    setIsMinting(true);
    setError('');
    try {
      const moodId = detectedMood.moodId;
      const tx = await contract.mint(account, moodId);
      
      console.log("Transaction sent:", tx.hash);
      await tx.wait(); // Wait for the transaction to be mined
      
      console.log("NFT minted successfully!");
      setDetectedMood(null); // Clear the mood preview
      await loadUserNFTs(contract, account); // Refresh the NFT collection
    } catch (err) {
      const errorMessage = err.reason || err.message || "An unknown error occurred during minting.";
      setError(`Minting failed: ${errorMessage}`);
      console.error("Minting error:", err);
    } finally {
      setIsMinting(false);
    }
  };

  // --- AI & CAMERA INTERACTIONS ---

  const startCamera = useCallback(async () => {
    if (cameraStream) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraStream(stream);
    } catch (err) {
      setError("Camera access denied. Please enable camera permissions in your browser.");
      console.error(err);
    }
  }, [cameraStream]);

  const detectMood = async () => {
    if (!videoRef.current?.srcObject) {
      await startCamera();
      return;
    }
    
    setIsDetecting(true);
    setError('');
    
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const video = videoRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = canvas.toDataURL('image/jpeg');

    try {
      const response = await fetch('http://localhost:5000/detect_mood', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: imageData }),
      });

      const result = await response.json();

      if (result.face_detected) {
        setDetectedMood({
          emotion: result.emotion,
          confidence: result.confidence,
          allEmotions: result.all_emotions,
          moodId: MOOD_MAPPING[result.emotion] ?? 4, // Default to Neutral
        });
      } else {
        setError(result.message || 'No face detected. Please try again.');
      }
    } catch (err) {
      setError('Could not connect to the AI mood detector. Is the Python server running?');
      console.error("Mood detection API error:", err);
    } finally {
      setIsDetecting(false);
    }
  };

  // --- EFFECTS ---

  useEffect(() => {
    startCamera();
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [startCamera, cameraStream]);

  // --- RENDER ---
  
  return (
    <div className="App">
      <header className="App-header">
        <h1>🎭 AI Mood NFT Minter</h1>
        <p>Your emotion, immortalized on the blockchain.</p>
        {account ? (
          <div className="wallet-info">
            🟢 Connected: <span>{`${account.substring(0, 6)}...${account.substring(38)}`}</span>
          </div>
        ) : (
          <button onClick={connectWallet} disabled={isConnecting} className="connect-btn">
            {isConnecting ? 'Connecting...' : '🦊 Connect Wallet'}
          </button>
        )}
      </header>

      {error && <div className="error-banner"><p>⚠️ {error}</p></div>}

      <main className="main-content">
        <section className="mood-detection-section card">
          <h2>1. Detect Your Mood</h2>
          <div className="camera-container">
            <video ref={videoRef} autoPlay muted playsInline className="video-feed" />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
          <button onClick={detectMood} disabled={isDetecting || !cameraStream} className="action-btn">
            {isDetecting ? 'Analyzing...' : '📸 Detect Mood'}
          </button>
        </section>

        {detectedMood && (
          <section className="nft-preview-section card">
            <h2>2. Mint Your Mood</h2>
            <div className="mood-card" style={{ borderColor: MOOD_METADATA[detectedMood.moodId].color }}>
              <div className="mood-emoji">{MOOD_METADATA[detectedMood.moodId].emoji}</div>
              <h3>{detectedMood.emotion}</h3>
              <p>Confidence: {(detectedMood.confidence * 100).toFixed(1)}%</p>
              
              <div className="emotion-breakdown">
                {Object.entries(detectedMood.allEmotions || {}).map(([emotion, value]) => (
                  <div key={emotion} className="emotion-bar">
                    <span className="emotion-label">{emotion}</span>
                    <div className="bar-container"><div className="bar-fill" style={{ width: `${value * 100}%` }}></div></div>
                  </div>
                ))}
              </div>

              <button onClick={handleMintNFT} disabled={isMinting} className="action-btn mint-btn">
                {isMinting ? 'Minting...' : `✨ Mint ${detectedMood.emotion} NFT`}
              </button>
            </div>
          </section>
        )}

        <section className="nft-collection-section card">
          <h2>🏆 Your NFT Collection</h2>
          {isLoadingNFTs ? (
            <p>Loading your collection...</p>
          ) : userNFTs.length === 0 ? (
            <p>You haven't minted any Mood NFTs yet.</p>
          ) : (
            <div className="nft-grid">
              {userNFTs.map(nft => (
                <div key={nft.tokenId} className="nft-card" style={{ borderColor: MOOD_METADATA[nft.mood]?.color }}>
                  <div className="nft-emoji">{MOOD_METADATA[nft.mood]?.emoji}</div>
                  <h4>{MOOD_METADATA[nft.mood]?.name}</h4>
                  <p className="token-id">Token ID: {nft.tokenId}</p>
                  <p className="timestamp">Minted: {new Date(nft.timestamp * 1000).toLocaleDateString()}</p>
                  <a href={nft.tokenURI} target="_blank" rel="noopener noreferrer" className="metadata-link">
                    View on IPFS
                  </a>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
